/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseSettlementP3Page.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/settlements/p3 三回目精算 —— 設計上 DISABLED（spec §8.1
 *                「未確認功能 disabled + tooltip」/ §11.2 畫面 12）。
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  Q2 BLOCKER（CLAUDE.md §11）：三回目 W41/W40 公式（工程規格書 §6.5）尚未取得
 *  業主 sign-off。Q2 未解前三回目只能做 blocked skeleton —— **前端絕不計算 P3**。
 *  本頁渲染清楚的 disabled 狀態：
 *    - 說明 P3 待業主確認 W41/W40 公式;
 *    - 以唯讀方式列出公式 placeholder（不放實際係數,只標「待確認」）;
 *    - 試算按鈕 disabled + tooltip（spec §8.1）;
 *    - 提供「驗證後端守門」入口：呼叫 POST /cases/:id/settlements/p3/calculate,
 *      後端回 409 V12_THIRD_SETTLEMENT_FORMULA_BLOCKED → 以 toast 證明後端阻擋,
 *      不顯示任何計算結果。
 *  RBAC：讀 = marketplace.cases.view（PERMISSIONS.caseRead）;此頁無寫入路徑。
 */

import { useState, type ReactElement } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { calculateP3 } from '../api/settlements'
import { ApiError } from '../api/client'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Separator } from '../components/ui/separator'
import { toast } from '../components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** 三回目精算公式 placeholder（唯讀;不放實際係數,僅標待確認 cell 與用途）。 */
const FORMULA_PLACEHOLDERS: { cellKey: string; descKey: string }[] = [
  { cellKey: 'p3.formula.w41', descKey: 'p3.formula.w41Desc' },
  { cellKey: 'p3.formula.w40', descKey: 'p3.formula.w40Desc' },
]

export function CaseSettlementP3Page({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  // 已嘗試過後端守門驗證（避免重複噴 toast）。
  const [probed, setProbed] = useState(false)

  // 驗證後端守門：預期回 409 blocked,不會成功。
  const probeMutation = useMutation({
    mutationFn: () =>
      calculateP3(caseId, {
        manual_amount_jpy: 0,
        manual_amount_reason: 'p3-blocked-probe',
        acknowledge_formula_blockers: false,
      }),
    onSuccess: () => {
      // 設計上不應走到這裡;若後端竟回成功,提示異常需停下確認。
      toast.error(t('p3.probe.unexpected'))
    },
    onError: (error) => {
      setProbed(true)
      const code = error instanceof ApiError ? error.body?.error.code : undefined
      if (code === 'V12_THIRD_SETTLEMENT_FORMULA_BLOCKED') {
        toast.success(t('p3.probe.confirmedBlocked'))
      } else {
        const detail = error instanceof ApiError ? `（${error.message}）` : ''
        toast.info(`${t('p3.probe.blockedGeneric')}${detail}`)
      }
    },
  })

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.p3')}</span>
        <h2 className="text-lg font-semibold">{t('p3.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('p3.subtitle')}</p>
      </header>

      {/* Q2 blocker 說明 */}
      <Card className="border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Badge variant="outline">{t('status.blocked')}</Badge>
            {t('p3.blocked.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>{t('p3.blocked.body')}</p>
          <p className="text-muted-foreground">{t('p3.blocked.q2')}</p>
        </CardContent>
      </Card>

      {/* 公式 placeholder（唯讀）*/}
      <Card>
        <CardHeader>
          <CardTitle>{t('p3.formula.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('p3.formula.hint')}</p>
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('p3.formula.cell')}</TableHead>
                  <TableHead>{t('p3.formula.purpose')}</TableHead>
                  <TableHead className="text-right">{t('p3.formula.value')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {FORMULA_PLACEHOLDERS.map((row) => (
                  <TableRow key={row.cellKey}>
                    <TableCell className="font-medium">{t(row.cellKey)}</TableCell>
                    <TableCell className="text-muted-foreground">{t(row.descKey)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{t('p3.formula.pending')}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Separator />

          {/* 試算按鈕：disabled by design + tooltip（spec §8.1）*/}
          <div className="space-y-2">
            <span
              className="inline-block"
              title={t('rbac.disabledTooltip')}
              aria-label={t('rbac.disabledTooltip')}
            >
              <Button type="button" disabled aria-disabled="true">
                {t('p3.calc.button')}
              </Button>
            </span>
            <p className="text-xs text-muted-foreground">{t('p3.calc.disabledHint')}</p>
          </div>

          <Separator />

          {/* 後端守門驗證（呼叫 endpoint 證明回 blocked,不顯示計算結果）*/}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('p3.probe.title')}</p>
            <p className="text-xs text-muted-foreground">{t('p3.probe.hint')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={probeMutation.isPending || caseId === ''}
              onClick={() => probeMutation.mutate()}
            >
              {probeMutation.isPending ? t('p3.probe.running') : t('p3.probe.button')}
            </Button>
            {probed ? (
              <p className="text-xs text-muted-foreground">{t('p3.probe.done')}</p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
